import { useMemo, useState } from 'react';
import {
  BoxArrowUp,
  Check,
  DotsThree,
  Shuffle,
  TrashSimple,
  Waves,
} from '@phosphor-icons/react';
import { Button, Dropdown, Flex, Image, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import type {
  AllocationMarkedTracker,
  GroupedWaveAllocation,
  WaveAllocation,
} from 'types/wave/detail';
import { MuteText } from '../../elements';
import {
  CommonConstant,
  CommonUtility,
  DateFormat,
  DateUtility,
  theme,
} from '../../utility';
import { ImageSizes } from '@utility/constants/style-contants';
import { OrderTime } from './common';
import { getSelectableAllocationIds } from './cancellationAlerts';

const { Text } = Typography;

type WaveDetailAllocationsTableProps = {
  allocations: GroupedWaveAllocation[];
  loading: boolean;
  itemMarkedTracker: AllocationMarkedTracker;
  isWaveInBuilding: boolean;
  cancellationAllocationIds?: Set<string>;
  selectedAllocationIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  removeAllocation: (item: GroupedWaveAllocation) => void;
  cancelAllocation: (item: GroupedWaveAllocation) => void;
  changeAllocation: (item: GroupedWaveAllocation) => void;
  assignAllocation: (item: GroupedWaveAllocation) => void;
};

function CancellationTag({
  allocationId,
  cancellationAllocationIds,
}: {
  allocationId: string;
  cancellationAllocationIds?: Set<string>;
}) {
  if (!cancellationAllocationIds?.has(allocationId)) {
    return null;
  }

  return (
    <Tag style={{ maxWidth: 'fit-content' }} color="error">
      Needs Cancellation
    </Tag>
  );
}

function GroupedCancellationSummary({
  item,
  cancellationAllocationIds,
}: {
  item: GroupedWaveAllocation;
  cancellationAllocationIds?: Set<string>;
}) {
  if (!item.sameItems?.length || !cancellationAllocationIds?.size) {
    return null;
  }

  const affectedCount = item.sameItems.filter(sameItem =>
    cancellationAllocationIds.has(sameItem.id),
  ).length;

  if (!affectedCount) {
    return null;
  }

  return <Text type="danger">{affectedCount} need cancellation</Text>;
}

function AllocationTimeCell({ allocatedAt }: { allocatedAt?: string | Date }) {
  if (!allocatedAt) {
    return null;
  }

  return (
    <Flex vertical gap={0}>
      <OrderTime time={allocatedAt} isEnabled />
      <MuteText>{DateUtility.formatDate(allocatedAt, DateFormat.barChartMonth)}</MuteText>
    </Flex>
  );
}

function ProductCell({
  item,
  indent = false,
}: {
  item: WaveAllocation;
  indent?: boolean;
}) {
  const product = item.inventory?.product;
  const imageUrl = product?.images?.display_image;

  return (
    <Flex align="flex-start" gap={12} style={indent ? { paddingLeft: 24 } : undefined}>
      <Image
        src={imageUrl || CommonConstant.defaultImage}
        fallback={CommonConstant.defaultImage}
        alt={product?.name}
        width={ImageSizes.medium}
        height={ImageSizes.medium}
        preview={Boolean(imageUrl)}
        style={{ objectFit: 'contain', flexShrink: 0 }}
      />
      <Flex vertical>
        <Text>{product?.name}</Text>
        <Text type="secondary">SKU {product?.sku}</Text>
      </Flex>
    </Flex>
  );
}

// antd 6 positions a Spin nested inside another Spin (the Table's loading
// wrapper) absolutely at the centre of the cell, over the status text.
// Keep these inline spinners in the flex flow.
const inlineSpinStyle = { position: 'static', transform: 'none' } as const;

function StatusCell({
  item,
  itemMarkedTracker,
  isWaveInBuilding,
}: {
  item: WaveAllocation;
  itemMarkedTracker: AllocationMarkedTracker;
  isWaveInBuilding: boolean;
}) {
  const status = itemMarkedTracker[item.id];

  if (status === 'loading') {
    return <Spin size="small" style={inlineSpinStyle} />;
  }

  if (status === 'picked') {
    return (
      <Flex align="center" justify="center" gap={8}>
        <Check size={16} />
        <span>Picked</span>
      </Flex>
    );
  }

  return (
    <Flex align="center" justify="center" gap={8}>
      {isWaveInBuilding && <Spin size="small" style={inlineSpinStyle} />}
      {CommonUtility.removeUnderscoreAndCapitalize(status)}
    </Flex>
  );
}

function getActionItems(
  item: WaveAllocation,
  itemMarkedTracker: AllocationMarkedTracker,
  handlers: Pick<
    WaveDetailAllocationsTableProps,
    'changeAllocation' | 'assignAllocation' | 'removeAllocation' | 'cancelAllocation'
  >,
): MenuProps['items'] {
  const status = itemMarkedTracker[item.id];
  const items: MenuProps['items'] = [];

  if (!['completed', 'sorted'].includes(status)) {
    items.push(
      {
        key: 'change-location',
        label: 'Change Location',
        icon: <Shuffle size={16} />,
        onClick: () => handlers.changeAllocation(item),
      },
      {
        key: 'move-wave',
        label: 'Move to another wave',
        icon: <Waves size={16} />,
        onClick: () => handlers.assignAllocation(item),
      },
      {
        key: 'remove',
        label: 'Remove Allocation',
        icon: <BoxArrowUp size={16} />,
        danger: true,
        onClick: () => handlers.removeAllocation(item),
      },
    );
  }

  items.push({
    key: 'cancel',
    label: 'Cancel Allocation',
    icon: <TrashSimple size={16} />,
    danger: true,
    onClick: () => handlers.cancelAllocation(item),
  });

  return items;
}

function ActionsCell({
  item,
  itemMarkedTracker,
  isWaveInBuilding,
  handlers,
}: {
  item: WaveAllocation;
  itemMarkedTracker: AllocationMarkedTracker;
  isWaveInBuilding: boolean;
  handlers: Pick<
    WaveDetailAllocationsTableProps,
    'changeAllocation' | 'assignAllocation' | 'removeAllocation' | 'cancelAllocation'
  >;
}) {
  return (
    <Flex align="center" justify="center" gap={8}>
      <StatusCell
        item={item}
        itemMarkedTracker={itemMarkedTracker}
        isWaveInBuilding={isWaveInBuilding}
      />
      <Dropdown
        menu={{ items: getActionItems(item, itemMarkedTracker, handlers) }}
        trigger={['click']}
      >
        <Button icon={<DotsThree size={18} />} />
      </Dropdown>
    </Flex>
  );
}

function buildChildColumns(
  props: WaveDetailAllocationsTableProps,
  showLocation: boolean,
): ColumnsType<WaveAllocation> {
  const handlers = {
    changeAllocation: props.changeAllocation,
    assignAllocation: props.assignAllocation,
    removeAllocation: props.removeAllocation,
    cancelAllocation: props.cancelAllocation,
  };

  const locationColumns: ColumnsType<WaveAllocation> = showLocation
    ? [
        {
          title: 'Zone',
          key: 'zone',
          render: (_, item) => (
            <Tag color={item?.inventory?.zone?.color}>{item?.inventory?.zone?.code}</Tag>
          ),
        },
        {
          title: 'Rack',
          key: 'rack',
          render: (_, item) => (
            <Tag color={item?.inventory?.zone?.color}>{item?.inventory?.rack?.code}</Tag>
          ),
        },
        {
          title: 'Location',
          key: 'location',
          render: (_, item) => (
            <Tag color={item?.inventory?.zone?.color}>
              {item?.inventory?.location?.code}
            </Tag>
          ),
        },
      ]
    : [];

  return [
    {
      title: 'Product Information',
      key: 'item',
      render: (_, item) => <ProductCell item={item} indent />,
    },
    {
      title: 'Allocation Time',
      key: 'allocation_time',
      render: (_, item) => <AllocationTimeCell allocatedAt={item.allocatedAt} />,
    },
    ...locationColumns,
    {
      title: 'Details',
      key: 'details',
      render: (_, item) => (
        <Flex vertical gap={4}>
          <Text>Qty: {item.quantity} Unit(s)</Text>
          <Text type="secondary">Order ID: {item.order?.displayId}</Text>
          <CancellationTag
            allocationId={item.id}
            cancellationAllocationIds={props.cancellationAllocationIds}
          />
        </Flex>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 160,
      render: (_, item) => (
        <ActionsCell
          item={item}
          itemMarkedTracker={props.itemMarkedTracker}
          isWaveInBuilding={props.isWaveInBuilding}
          handlers={handlers}
        />
      ),
    },
  ];
}

function ChildAllocationsTable({
  items,
  showLocation,
  selectedAllocationIds = [],
  onSelectionChange,
  ...props
}: WaveDetailAllocationsTableProps & {
  items: WaveAllocation[];
  showLocation: boolean;
}) {
  const columns = useMemo(
    () => buildChildColumns(props, showLocation),
    [props, showLocation],
  );

  const selectedSet = useMemo(
    () => new Set(selectedAllocationIds),
    [selectedAllocationIds],
  );

  const updateSelection = (ids: string[], selected: boolean) => {
    if (!onSelectionChange) return;

    const next = new Set(selectedAllocationIds);
    ids.forEach(id => {
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
    });
    onSelectionChange(Array.from(next));
  };

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={items}
      pagination={false}
      size="small"
      rowSelection={
        onSelectionChange
          ? {
              selectedRowKeys: items
                .map(item => item.id)
                .filter(id => selectedSet.has(id)),
              onSelect: (record, selected) => updateSelection([record.id], selected),
              onSelectAll: (selected, _selectedRows, changeRows) => {
                updateSelection(
                  changeRows.map(row => row.id),
                  selected,
                );
              },
            }
          : undefined
      }
    />
  );
}

export function WaveDetailAllocationsTable({
  allocations,
  loading,
  itemMarkedTracker,
  isWaveInBuilding,
  cancellationAllocationIds,
  selectedAllocationIds = [],
  onSelectionChange,
  removeAllocation,
  cancelAllocation,
  changeAllocation,
  assignAllocation,
}: WaveDetailAllocationsTableProps) {
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const selectedSet = useMemo(
    () => new Set(selectedAllocationIds),
    [selectedAllocationIds],
  );

  const selectedParentRowKeys = useMemo(
    () =>
      allocations
        .filter(record => {
          const leafIds = getSelectableAllocationIds(record);
          return leafIds.every(id => selectedSet.has(id));
        })
        .map(record => record.id),
    [allocations, selectedSet],
  );

  const updateSelection = (ids: string[], selected: boolean) => {
    if (!onSelectionChange) return;

    const next = new Set(selectedAllocationIds);
    ids.forEach(id => {
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
    });
    onSelectionChange(Array.from(next));
  };

  const expandableIds = useMemo(
    () =>
      allocations
        .filter(item => CommonUtility.isValidArray(item.sameItems))
        .map(item => item.id),
    [allocations],
  );

  const allExpanded =
    expandableIds.length > 0 && expandableIds.every(id => expandedRowKeys.includes(id));

  const handlers = {
    changeAllocation,
    assignAllocation,
    removeAllocation,
    cancelAllocation,
  };

  const columns: ColumnsType<GroupedWaveAllocation> = useMemo(
    () => [
      {
        title: 'Product Information',
        key: 'item',
        render: (_, item) => <ProductCell item={item} />,
      },
      {
        title: 'Allocation Time',
        key: 'allocation_time',
        render: (_, item) =>
          CommonUtility.isValidArray(item.sameItems) ? (
            <Text type="secondary">Multiple</Text>
          ) : (
            <AllocationTimeCell allocatedAt={item.allocatedAt} />
          ),
      },
      {
        title: 'Zone',
        key: 'zone',
        render: (_, item) => {
          if (CommonUtility.isValidArray(item.sameItems)) {
            const multipleLocations = item.sameItems.some(
              sameItem =>
                sameItem?.inventory?.location?.id !== item?.inventory?.location?.id,
            );
            if (multipleLocations) {
              return <Text type="secondary">Multiple Locations</Text>;
            }
          }
          return (
            <Tag color={item?.inventory?.zone?.color}>{item?.inventory?.zone?.code}</Tag>
          );
        },
      },
      {
        title: 'Rack',
        key: 'rack',
        render: (_, item) => {
          if (CommonUtility.isValidArray(item.sameItems)) {
            const multipleLocations = item.sameItems.some(
              sameItem =>
                sameItem?.inventory?.location?.id !== item?.inventory?.location?.id,
            );
            if (multipleLocations) return null;
          }
          return (
            <Tag color={item?.inventory?.zone?.color}>{item?.inventory?.rack?.code}</Tag>
          );
        },
      },
      {
        title: 'Location',
        key: 'location',
        render: (_, item) => {
          if (CommonUtility.isValidArray(item.sameItems)) {
            const multipleLocations = item.sameItems.some(
              sameItem =>
                sameItem?.inventory?.location?.id !== item?.inventory?.location?.id,
            );
            if (multipleLocations) return null;
          }
          return (
            <Tag color={item?.inventory?.zone?.color}>
              {item?.inventory?.location?.code}
            </Tag>
          );
        },
      },
      {
        title: 'Details',
        key: 'details',
        render: (_, item) => {
          if (CommonUtility.isValidArray(item.sameItems)) {
            const totalQty = item.sameItems.reduce(
              (total, sameItem) => total + (sameItem.quantity || 0),
              0,
            );

            return (
              <Flex vertical gap={4}>
                <Text type="secondary">Total Qty: {totalQty}</Text>
                <GroupedCancellationSummary
                  item={item}
                  cancellationAllocationIds={cancellationAllocationIds}
                />
              </Flex>
            );
          }

          return (
            <Flex vertical gap={4}>
              <Text>Qty: {item.quantity} Unit(s)</Text>
              <Text type="secondary">Order ID: {item.order?.displayId}</Text>
              <CancellationTag
                allocationId={item.id}
                cancellationAllocationIds={cancellationAllocationIds}
              />
            </Flex>
          );
        },
      },
      {
        title: expandableIds.length ? (
          <Button
            type="link"
            size="small"
            onClick={() => setExpandedRowKeys(allExpanded ? [] : expandableIds)}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        ) : (
          ''
        ),
        key: 'actions',
        width: 160,
        render: (_, item) => {
          if (CommonUtility.isValidArray(item.sameItems)) {
            return null;
          }

          return (
            <ActionsCell
              item={item}
              itemMarkedTracker={itemMarkedTracker}
              isWaveInBuilding={isWaveInBuilding}
              handlers={handlers}
            />
          );
        },
      },
    ],
    [
      allExpanded,
      expandableIds,
      itemMarkedTracker,
      isWaveInBuilding,
      handlers,
      cancellationAllocationIds,
    ],
  );

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={allocations}
      loading={loading}
      style={{
        borderRadius: 6,
        border: `1px solid ${theme.colors.semanticBorder}`,
      }}
      pagination={false}
      locale={{ emptyText: 'No Allocations Found' }}
      rowSelection={
        onSelectionChange
          ? {
              selectedRowKeys: selectedParentRowKeys,
              onSelect: (record, selected) =>
                updateSelection(getSelectableAllocationIds(record), selected),
              onSelectAll: (selected, _selectedRows, changeRows) => {
                const ids = changeRows.flatMap(record =>
                  getSelectableAllocationIds(record),
                );
                updateSelection(ids, selected);
              },
            }
          : undefined
      }
      expandable={{
        expandedRowKeys,
        onExpandedRowsChange: keys => setExpandedRowKeys(keys as string[]),
        rowExpandable: record => CommonUtility.isValidArray(record.sameItems),
        expandedRowRender: record => {
          const multipleLocations = record.sameItems?.some(
            sameItem =>
              sameItem?.inventory?.location?.id !== record?.inventory?.location?.id,
          );

          return (
            <ChildAllocationsTable
              items={record.sameItems || []}
              showLocation={!!multipleLocations}
              allocations={allocations}
              loading={loading}
              itemMarkedTracker={itemMarkedTracker}
              isWaveInBuilding={isWaveInBuilding}
              cancellationAllocationIds={cancellationAllocationIds}
              selectedAllocationIds={selectedAllocationIds}
              onSelectionChange={onSelectionChange}
              removeAllocation={removeAllocation}
              cancelAllocation={cancelAllocation}
              changeAllocation={changeAllocation}
              assignAllocation={assignAllocation}
            />
          );
        },
      }}
    />
  );
}
